<script setup>
import { computed, inject, ref, watch } from 'vue';
import { RouterLink, useRouter } from 'vue-router';
import XlButton from '../xl/XlButton.vue';
import XlPopover from '../xl/XlPopover.vue';
import XlTag from '../xl/XlTag.vue';
import PortalJoinButton from './PortalJoinButton.vue';
import IconClose from '../icons/IconClose.vue';
import IconRepeat from '../icons/IconRepeat.vue';
import { writeSeat } from '../../helpers/sessionSeat';
import { minutesLabel } from '../../helpers/sessionCard';
import { speakingLook } from '../../helpers/speakerConsent';
import {
  dayKey, dayMonth, peopleLine, seriesLabel, timeRange, timeState, windowUntil,
} from '../../helpers/sessionTimes';

/**
 * What a policyholder sees on clicking a chip in the week (XN-4558): the
 * prototype's client calPop (build 30 Sep 12:40), worded for members. A
 * session offers its seat for that date, or Join once it is on, and says
 * when it is one of a series. Office Hours is a drop-in: its window, whoever
 * is on (their own broker or not), open or closed — and one action, Join now,
 * only while it is open.
 */
const props = defineProps({
  open: { type: Boolean, default: false },
  anchor: { type: [Object, null], default: null },
  boundary: { type: [Object, null], default: null },
  session: { type: [Object, null], default: null },
  now: { type: Date, required: true },
  timeZone: { type: String, required: true },
});
const emit = defineEmits(['update:open', 'seat', 'gone']);

const room = inject('portalRoom', null);
const router = useRouter();

const KIND_LINES = {
  video_conference: 'Everyone can speak — turn your camera on if you like, or just listen.',
  webinar: 'You watch and listen, and can type questions as it goes.',
  office_hours: 'No appointment, no agenda. Ask about a claim, your renewal, or anything you\'re unsure of.',
};

const isDesk = computed(() => props.session?.kind === 'office_hours');
const title = computed(() => {
  if (!isDesk.value) return props.session?.title ?? '';
  const first = String(props.session?.host?.name ?? '').trim().split(/\s+/)[0];
  return first ? `Talk to ${first}` : 'Office Hours';
});
const when = computed(() => {
  const { starts_at: start, ends_at: end } = props.session;
  // A drop-in is open for a window; a session runs from one time to another.
  const span = isDesk.value
    ? windowUntil(start, end, props.timeZone)
    : timeRange(start, end, props.timeZone);
  return `${dayMonth(start, props.timeZone)} · ${span}`;
});
const state = computed(() => timeState(props.session, props.now));
const speak = computed(() => speakingLook(props.session?.speaking));
const openInvitation = () => {
  router?.push(props.session.speaking?.invitation_path ?? `/hub/sessions/${props.session.id}/speaking`);
  close();
};
const people = computed(() => peopleLine(props.session));
// A drop-in has no length to state.
const length = computed(() => (isDesk.value ? null : minutesLabel(props.session?.minutes)));
const repeats = computed(() => (props.session?.series ? seriesLabel(props.session.series) : null));

// Open is inside its window with somebody on the desk.
const deskOpen = computed(() => isDesk.value && state.value.live && Boolean(String(props.session?.host?.name ?? '').trim()));
// The prototype's footer words (calPop, build 7 Oct).
const deskStatus = computed(() => {
  if (!isDesk.value) return null;
  if (!String(props.session?.host?.name ?? '').trim()) return 'Nobody on the desk — closed';
  if (deskOpen.value) return 'Open right now';
  if (state.value.ended) return 'Closed';
  if (dayKey(props.session.starts_at, props.timeZone) === dayKey(props.now.toISOString(), props.timeZone)) return 'Later today';

  return "Opens on the day — there's nothing to book";
});

// Shown at once and put back if the write fails; follows the session when another chip is opened,
// but never mid-write, when it belongs to the session being written.
const seat = ref(false);
const busy = ref(false);
watch(
  () => [props.session?.id, props.session?.seat],
  () => { if (!busy.value) seat.value = Boolean(props.session?.seat); },
  { immediate: true },
);

// The popover unmounts with focus inside it, so hand focus back to the chip.
const close = () => {
  emit('update:open', false);
  props.anchor?.focus?.();
};

async function toggleSeat() {
  if (busy.value) return;
  const { id } = props.session;
  const on = !seat.value;
  busy.value = true;
  seat.value = on;
  const outcome = await writeSeat(id, on, { series: false });
  busy.value = false;

  // The page patches its entries by id, so it hears about this write whichever chip is shown now.
  const shown = props.session?.id === id;
  if (outcome === 'saved' || outcome === 'released') emit('seat', { id, seat: on });
  else if (outcome === 'gone') emit('gone', id);

  // The toggle belongs to the shown session; a write for another one leaves it alone.
  if (!shown) {
    seat.value = Boolean(props.session?.seat);
    return;
  }
  if (outcome === 'saved' || outcome === 'released') return;
  seat.value = !on;
  if (outcome === 'gone') close();
}

function joinCall() {
  const { id } = props.session;
  close();
  room?.join(id);
}
</script>

<template>
  <XlPopover
    :open="open && !!session"
    :anchor="anchor"
    :boundary="boundary"
    labelled-by="portal-popover-title"
    @update:open="emit('update:open', $event)"
  >
    <template v-if="session">
      <header class="pop__head" :class="{ 'pop__head--desk': isDesk }">
        <div class="pop__titles">
          <b id="portal-popover-title" class="pop__title">
            {{ title }}
            <XlTag v-if="isDesk" tone="success" data-testid="popover-dropin">Drop-in</XlTag>
          </b>
          <span class="pop__when">{{ when }}</span>
        </div>
        <button type="button" class="pop__close" aria-label="Close" @click="close">
          <IconClose color="currentColor" />
        </button>
      </header>

      <div class="pop__body">
        <p class="pop__line">{{ KIND_LINES[session.kind] }}</p>
        <p v-if="people || length" class="pop__row">
          <b v-if="people">{{ people }}</b>
          <span v-if="length">{{ length }}</span>
        </p>
        <p v-if="!isDesk && session.recorded" class="pop__row">
          <b>Recorded — can't make it? Watch it back afterwards.</b>
        </p>
        <p v-if="repeats" class="pop__row" data-testid="popover-repeats">
          <IconRepeat color="currentColor" size="13" /><span>{{ repeats }}</span>
        </p>
        <p v-if="deskStatus" class="pop__row pop__status" :class="{ 'pop__status--open': deskOpen }" data-testid="popover-desk-status">
          {{ deskStatus }}
        </p>
      </div>

      <footer class="pop__foot">
        <!-- A drop-in has one action, and only while it is open: join the call. -->
        <template v-if="isDesk">
          <XlButton v-if="deskOpen" variant="success" size="sm" data-testid="popover-desk-join" @click="joinCall">
            Join now<span class="pop__sr">: Office Hours</span>
          </XlButton>
        </template>
        <template v-else>
          <template v-if="!state.ended">
            <XlButton
              v-if="!state.joinable && !speak"
              :variant="seat ? 'ghost' : 'filled'"
              size="sm"
              data-testid="popover-seat"
              @click="toggleSeat"
            >{{ seat ? 'Seat saved' : 'Save my seat' }}<span class="pop__sr">: {{ session.title }}</span></XlButton>
            <!-- XN-4558: Join is always here, dimmed until 15 minutes before. -->
            <XlButton
              v-if="speak?.asked"
              variant="filled"
              size="sm"
              data-testid="read-invitation"
              @click="openInvitation"
            >Read the invitation<span class="pop__sr">: {{ session.title }}</span></XlButton>
            <PortalJoinButton
              v-else
              :speaker="Boolean(speak?.speaking)"
              :label="speak?.speaking ? 'Join as a speaker' : 'Join'"
              :title="session.title"
              :joinable="state.joinable"
              @join="joinCall"
            />
          </template>
          <RouterLink :to="`/hub/sessions/${session.id}`" class="pop__link">Session details</RouterLink>
        </template>
      </footer>
    </template>
  </XlPopover>
</template>

<style scoped>
/* Prototype `.calpop` (build 30 Sep 12:40): a coloured left edge, body, footer.
   XlPopover already pads its panel 16px, so these sections add none of their own sideways. */
.pop__head {
  display: flex; align-items: flex-start; gap: 10px; padding: 0 0 12px 12px;
  border-left: 4px solid var(--xl-color-primary);
}
.pop__head--desk { border-left-color: #166B48; }
.pop__titles { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 2px; }
.pop__title { font-size: 15px; font-weight: 600; }
.pop__when { font-size: 12.5px; color: var(--xl-color-secondary-text); }
.pop__close {
  width: 28px; height: 28px; flex: 0 0 28px; display: grid; place-items: center;
  border: 0; background: none; border-radius: 8px; cursor: pointer; color: var(--xl-color-secondary-text);
}
.pop__close:focus-visible { outline: 2px solid var(--xl-color-primary); outline-offset: 2px; }
.pop__body { padding: 0 0 8px; }
.pop__line { margin: 0 0 10px; font-size: 13.5px; line-height: 1.55; color: var(--xl-color-secondary-text); }
.pop__row { margin: 0 0 8px; display: flex; flex-direction: column; font-size: 13px; color: var(--xl-color-secondary-text); }
.pop__row b { font-size: 13.5px; color: var(--xl-color-on-surface); }
.pop__row[data-testid="popover-repeats"] { flex-direction: row; align-items: center; gap: 6px; }
.pop__status { font-weight: 600; }
.pop__status--open { color: #166B48; }
.pop__title { display: inline-flex; align-items: center; gap: 8px; flex-wrap: wrap; }
/* Actions sit left; the link to the session's page keeps the right edge, alone or not (XN-4734). */
.pop__foot {
  display: flex; align-items: center; justify-content: flex-start; gap: 10px; flex-wrap: wrap;
  padding: 12px 0 0; border-top: 1px solid var(--xl-color-border);
}
.pop__link { margin-left: auto; font-size: 13.5px; font-weight: 600; color: var(--xl-color-primary); text-decoration: none; }
.pop__link:focus-visible { outline: 2px solid var(--xl-color-primary); outline-offset: 2px; }
.pop__sr {
  position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px; overflow: hidden;
  clip: rect(0 0 0 0); white-space: nowrap; border: 0;
}
</style>
